import * as React from "react"
import * as AvatarPrimitive from "@radix-ui/react-avatar"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Avatar — Flozy Design System
 *
 * Sizes (Foundations/Avatar):
 *   xs 24 · sm 32 · md 40 · lg 56
 *
 * Initials fallback uses one of five stable tones (blue / red / green / amber / gray).
 * Use `avatarToneFor(name)` to hash a name → tone, so the same person always renders
 * the same color across the app.
 */

const avatarVariants = cva(
  "relative inline-flex shrink-0 overflow-hidden rounded-full bg-muted text-muted-foreground font-semibold select-none",
  {
    variants: {
      size: {
        xs: "size-6 text-[10px]",
        sm: "size-8 text-[12px]",
        md: "size-10 text-[14px]",
        lg: "size-14 text-[18px]",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
)

const fallbackToneClass: Record<AvatarTone, string> = {
  blue:  "bg-[var(--flozy-blue-50)] text-[var(--flozy-blue-700)]",
  red:   "bg-[var(--color-error-bg)] text-[var(--color-error-text)]",
  green: "bg-[var(--color-success-bg)] text-[var(--color-success-text)]",
  amber: "bg-[var(--color-warning-bg)] text-[var(--color-warning-text)]",
  gray:  "bg-[var(--gray-100)] text-[var(--gray-700)]",
}

export type AvatarTone = "blue" | "red" | "green" | "amber" | "gray"

const TONES: AvatarTone[] = ["blue", "red", "green", "amber", "gray"]

/** Hash a string to a stable avatar tone — same input always returns same tone. */
export function avatarToneFor(name: string | undefined | null): AvatarTone {
  if (!name) return "gray"
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0
  return TONES[Math.abs(h) % TONES.length]
}

/** Derive 1- or 2-letter initials from a name. */
export function initialsFor(name: string | undefined | null): string {
  if (!name) return "?"
  return name
    .trim()
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()
}

function Avatar({
  className,
  size,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      data-size={size ?? "sm"}
      className={cn(avatarVariants({ size }), className)}
      {...props}
    />
  )
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full object-cover", className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  tone = "gray",
  children,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback> & {
  tone?: AvatarTone
}) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      data-tone={tone}
      className={cn(
        "flex size-full items-center justify-center rounded-full",
        fallbackToneClass[tone],
        className,
      )}
      {...props}
    >
      {children}
    </AvatarPrimitive.Fallback>
  )
}

export { Avatar, AvatarImage, AvatarFallback, avatarVariants }
